import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format, subDays } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { canViewClaudeStats } from '@/lib/permissions';
import { useAdminRole } from '@/hooks/useAdminRole';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

interface DailyStats {
  date: string;
  tokens: number;
  requests: number;
  cost: number;
  inputTokens: number;
  outputTokens: number;
}

interface ClaudeAPIStats {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_cost: number;
}

export function ClaudeStatsPage() {
  const [usageHistory, setUsageHistory] = useState<DailyStats[]>([]);
  const [apiStats, setApiStats] = useState<ClaudeAPIStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { adminRole } = useAdminRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && adminRole && !canViewClaudeStats(adminRole)) {
      navigate('/admin/customers');
      return;
    }
    
    if (adminRole && canViewClaudeStats(adminRole)) {
      fetchStats();
    }
  }, [adminRole]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch Claude API stats
      const response = await fetch('/api/claude/stats', {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch Claude API stats');
      const claudeStats = await response.json();
      setApiStats(claudeStats);

      // Fetch usage history for the last 30 days
      const thirtyDaysAgo = subDays(new Date(), 30);
      const { data: usage } = await supabase
        .from('claude_api_usage')
        .select('*')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (usage) {
        const dailyStats = usage.reduce((acc: DailyStats[], curr) => {
          const date = format(new Date(curr.created_at), 'yyyy-MM-dd');
          const existingDay = acc.find(day => day.date === date);
          
          if (existingDay) {
            existingDay.tokens += curr.tokens_used;
            existingDay.requests += curr.request_count;
            existingDay.cost = existingDay.tokens * 0.00001102;
            existingDay.inputTokens += curr.input_tokens || 0;
            existingDay.outputTokens += curr.output_tokens || 0;
          } else {
            acc.push({
              date,
              tokens: curr.tokens_used,
              requests: curr.request_count,
              cost: curr.tokens_used * 0.00001102,
              inputTokens: curr.input_tokens || 0,
              outputTokens: curr.output_tokens || 0
            });
          }
          
          return acc;
        }, []);

        setUsageHistory(dailyStats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Claude API Usage History</h1>
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!adminRole || !canViewClaudeStats(adminRole)) {
    return null;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Claude API Usage History</h1>
      
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>Input Tokens</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{apiStats?.input_tokens.toLocaleString()}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Output Tokens</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{apiStats?.output_tokens.toLocaleString()}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Total Tokens</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{apiStats?.total_tokens.toLocaleString()}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Total Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${apiStats?.total_cost.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily Token Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageHistory}>
                <defs>
                  <linearGradient id="inputTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="outputTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--secondary))" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="hsl(var(--secondary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => format(new Date(date), 'MMM d')}
                />
                <YAxis />
                <Tooltip
                  formatter={(value: number) => value.toLocaleString()}
                  labelFormatter={(date) => format(new Date(date), 'MMMM d, yyyy')}
                />
                <Area
                  type="monotone"
                  dataKey="inputTokens"
                  name="Input Tokens"
                  stroke="hsl(var(--primary))"
                  fillOpacity={1}
                  fill="url(#inputTokens)"
                />
                <Area
                  type="monotone"
                  dataKey="outputTokens"
                  name="Output Tokens"
                  stroke="hsl(var(--secondary))"
                  fillOpacity={1}
                  fill="url(#outputTokens)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {usageHistory.map((day) => (
          <Card key={day.date}>
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold">{format(new Date(day.date), 'MMMM d, yyyy')}</p>
                  <p className="text-sm text-muted-foreground">
                    {day.requests.toLocaleString()} requests
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    Input: {day.inputTokens.toLocaleString()} / Output: {day.outputTokens.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    ${day.cost.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}